import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material';
import { ListItemIcon, Menu, MenuItem, styled, TableCell, Tooltip, Typography } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import type { TableOptions } from '@tanstack/react-table';

import { IconButton } from 'components/mui/common/IconButton';
import type { DataSchema, Schema } from 'types';
import { getIcon } from 'utils/common';
export const StyledTableCell = styled(TableCell)(() => ({
  padding: '8px',
}));

interface Props<T extends keyof P, P extends DataSchema> {
  controls?: {
    rowId: string;
    controls: {
      id: string;
      alias: string;
      tooltip: string;
      iconName?: string;
      color?: string;
      onClick: () => void;
    }[];
  }[];
  row: TableOptions<Schema<T, P>>['data'][number] & { id: string; actions?: { copyInRow?: boolean; copy?: boolean } };
  noTableCell?: boolean;
  sx?: SxProps<Theme>;
  testIdPrefix?: string;
}

const TableRowControlsBase = <T extends keyof P, P extends DataSchema>({ controls, row, noTableCell, sx, testIdPrefix }: Props<T, P>) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const functionsControls = useMemo(() => controls?.find((control) => control.rowId === row.id)?.controls, [controls, row.id]);

  const content = (
    <>
      <IconButton
        onClick={handleClick}
        sx={sx}
        data-testid={testIdPrefix ? `${testIdPrefix}-functions-menu-button` : 'table-row-functions-menu-button'}>
        <Tooltip title={t('helios.functions.title')}>
          <PlayArrow />
        </Tooltip>
      </IconButton>
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(43,54,116,0.12), 0 1px 4px rgba(43,54,116,0.08)',
              border: '1px solid rgba(43,54,116,0.08)',
              minWidth: '160px',
              mt: '4px',
            },
          },
        }}
        MenuListProps={{ sx: { py: '6px' } }}>
        {functionsControls?.length ? (
          functionsControls.map(({ id, onClick, tooltip, alias, color, iconName }) => {
            const IconComp = iconName ? getIcon(iconName) : null;
            return (
              <MenuItem
                key={id}
                onClick={onClick}
                data-testid={testIdPrefix ? `${testIdPrefix}-function-item-${id}` : `table-row-function-item-${id}`}
                sx={{
                  px: '14px',
                  py: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: color ?? 'text.primary',
                  '&:hover': { backgroundColor: 'rgba(39,72,251,0.06)' },
                }}>
                {IconComp && (
                  <ListItemIcon sx={color ? { color, minWidth: '32px' } : { minWidth: '32px' }}>
                    <IconComp fontSize="small" />
                  </ListItemIcon>
                )}
                <Tooltip title={tooltip}>
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 1.4, color: 'inherit' }}>{alias}</Typography>
                </Tooltip>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled sx={{ px: '14px', py: '8px' }}>
            <Typography sx={{ fontSize: '13px', fontStyle: 'italic', color: 'text.secondary' }}>
              {t('helios.functions.tooltips.noFunctions')}
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );

  if (noTableCell) return content;

  return (
    <StyledTableCell align="right" sx={{ ...sx }}>
      {content}
    </StyledTableCell>
  );
};

export const TableRowHeliosFunctionsControls = memo(TableRowControlsBase) as typeof TableRowControlsBase;
